import {useCallback, useMemo, useState} from 'react';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {useSearchParams} from 'react-router';
import {ApiError} from '../api/client';
import {invalidateLinkImportItems, useLinkImportFailedCount} from '../api/linkImports';
import {
  contentRequestQueryOptions,
  useContentUploadQueue,
  useSubmitContentRequest,
  type ContentRequestFile,
  type ContentRequestState,
} from '../api/contentRequests';
import {DriveLinkImport, failedImportsQuestion} from '../components/DriveLinkImport';
import {Dropzone} from '../components/Dropzone';
import {UploadQueue} from '../components/UploadQueue';
import {FullScreenLoader} from '../components/FullScreenLoader';
import {useWakeLock} from '../lib/useWakeLock';

// Сторінка дзеркальна до CustomUpload, але з двома UX-відмінностями:
//   1) кнопка має текст «Відправити контент-менеджеру» (а не адмінам)
//   2) лейбл стейтів ширше, бо потік без явної фази «опубліковано» для моделі —
//      `published` модель майже ніколи не побачить (їй посилання вже немає
//      сенсу відкривати), але рендеримо як success на всякий випадок.
export function ContentUpload() {
  const [searchParams] = useSearchParams();
  const contentRequestId = searchParams.get('contentRequestId');
  const token = searchParams.get('token');

  if (!contentRequestId || !token) {
    return <ErrorScreen title="Ссылка недействительна" hint="Откройте ссылку из бота заново." />;
  }
  return <ContentUploadInner contentRequestId={contentRequestId} token={token} />;
}

interface InnerProps {
  contentRequestId: string;
  token: string;
}

function ContentUploadInner({contentRequestId, token}: InnerProps) {
  const view = useQuery(contentRequestQueryOptions(contentRequestId, token));
  const queue = useContentUploadQueue(contentRequestId, token);
  const submit = useSubmitContentRequest(contentRequestId, token);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const qc = useQueryClient();
  // Идёт перенос с Google Диска: сдача закрыта (сервер ответит 409 link_import_active).
  const [linkImportActive, setLinkImportActive] = useState(false);
  const linkImportFailed = useLinkImportFailedCount('content-requests', contentRequestId, token);

  const onDrop = useCallback(
    (accepted: File[]) => queue.enqueue(accepted),
    [queue],
  );

  // Перенесённый файл лежит в папке запроса, но список в view об этом не знает.
  const onLinkImportFilesChanged = useCallback(() => {
    void qc.invalidateQueries({queryKey: ['content-request', contentRequestId]});
  }, [qc, contentRequestId]);

  const hasUploadsInFlight = useMemo(
    () => queue.items.some((i) => i.status === 'queued' || i.status === 'uploading'),
    [queue.items],
  );

  // Тримаємо екран «прокинутим» поки йдуть аплоади — інакше iOS Safari
  // призупиняє вкладку і XHR падає посередині. Той самий хук, що в основному
  // дашборді й у CustomUpload.
  useWakeLock(hasUploadsInFlight);

  if (view.isPending) return <FullScreenLoader />;

  if (view.isError) {
    const err = view.error;
    if (err instanceof ApiError && err.status === 403) {
      return <ErrorScreen title="Ссылка просрочена или недействительна" hint="Запросите новую ссылку у бота." />;
    }
    if (err instanceof ApiError && err.status === 404) {
      return <ErrorScreen title="Запрос не найден" hint="Возможно, его удалили. Свяжитесь с админом." />;
    }
    return <ErrorScreen title="Не удалось загрузить запрос" hint="Попробуйте обновить страницу." />;
  }

  const data = view.data;
  const isUploading = data.state === 'uploading';
  const fileCountFromDrive = data.files.length;
  const totalKnownFiles =
    fileCountFromDrive + queue.items.filter((i) => i.status !== 'error' && i.status !== 'done').length;
  // Як тільки модель тицьнула «Відправити» — ховаємо Dropzone і чергу,
  // не чекаючи refetch. Інакше до приходу нового state модель може ще раз
  // тапнути по дроп-зоні і додати файли, які сервер відмовить 409
  // not_uploading_state — UX-промах.
  const submitInProgress = submit.isPending || submit.isSuccess;
  const showUploader = isUploading && !submitInProgress;

  return (
    <div className="min-h-screen p-4 sm:p-6">
      <div className="max-w-3xl mx-auto space-y-8">
        <header className="space-y-1">
          <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">
            Запрос на контент
          </p>
          <h1 className="text-xl sm:text-2xl font-semibold">{data.title}</h1>
          <StateBadge state={data.state} />
          {data.description && (
            <p className="text-sm text-slate-700 whitespace-pre-wrap pt-2">{data.description}</p>
          )}
        </header>

        {showUploader && <Dropzone onDrop={onDrop} />}

        {showUploader && (
          <UploadQueue items={queue.items} onCancel={queue.cancel} onRetry={queue.retry} />
        )}

        {showUploader && data.linkImport && (
          <DriveLinkImport
            resource="content-requests"
            requestId={contentRequestId}
            token={token}
            onActiveChange={setLinkImportActive}
            onFilesChanged={onLinkImportFilesChanged}
          />
        )}

        <section className="space-y-3">
          <h2 className="text-lg font-semibold">Загруженные файлы</h2>
          <DriveFileList files={data.files} />
        </section>

        {/* SubmitPanel ховаємо одразу як submit пройшов — інакше до приходу
            свіжого state блимає success-нотіс панелі, потім підміняється
            нотісом нижче (data.state === 'submitted'). */}
        {isUploading && !submitInProgress && (
          <SubmitPanel
            disabled={totalKnownFiles === 0 || hasUploadsInFlight || submit.isPending || linkImportActive}
            pending={submit.isPending}
            onSubmit={() => setConfirmOpen(true)}
            error={submit.error}
            hasUploadsInFlight={hasUploadsInFlight}
            linkImportActive={linkImportActive}
            fileCount={fileCountFromDrive}
          />
        )}

        {(submit.isSuccess || data.state === 'submitted') && (
          <Notice tone="info">
            Контент отправлен контент-менеджеру. Дождитесь подтверждения.
          </Notice>
        )}
        {data.state === 'published' && (
          <Notice tone="success">Контент принят и залит на OnlyFans.</Notice>
        )}
      </div>

      {confirmOpen && (
        <ConfirmSubmitDialog
          failedCount={linkImportFailed}
          onConfirm={() => {
            setConfirmOpen(false);
            submit.mutate(undefined, {
              onError: (err) => {
                // Страница не знала о переносе (запущен из приложения) — подтянуть его.
                if (err instanceof ApiError && err.code === 'link_import_active') {
                  void invalidateLinkImportItems(qc, 'content-requests', contentRequestId);
                }
              },
            });
          }}
          onCancel={() => setConfirmOpen(false)}
        />
      )}
    </div>
  );
}

function ConfirmSubmitDialog({
  failedCount,
  onConfirm,
  onCancel,
}: {
  failedCount: number;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-sm text-slate-700">
          После отправки контент-менеджеру вы не сможете добавить контент по этому запросу.
        </p>
        <p className="text-base font-semibold text-slate-900">
          {failedCount > 0 ? failedImportsQuestion(failedCount) : 'Вы уверены?'}
        </p>
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100"
          >
            Нет
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
          >
            Да
          </button>
        </div>
      </div>
    </div>
  );
}

interface SubmitPanelProps {
  disabled: boolean;
  pending: boolean;
  onSubmit: () => void;
  error: unknown;
  hasUploadsInFlight: boolean;
  linkImportActive: boolean;
  fileCount: number;
}

function SubmitPanel({
  disabled,
  pending,
  onSubmit,
  error,
  hasUploadsInFlight,
  linkImportActive,
  fileCount,
}: SubmitPanelProps) {
  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={onSubmit}
        disabled={disabled}
        className="w-full sm:w-auto px-5 py-2.5 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {pending ? 'Отправляем…' : 'Отправить контент-менеджеру'}
      </button>
      {hasUploadsInFlight && (
        <p className="text-xs text-slate-500">
          Дождитесь окончания загрузки активных файлов.
        </p>
      )}
      {linkImportActive && (
        <p className="text-xs text-slate-500">Идёт перенос с Google Диска</p>
      )}
      {!hasUploadsInFlight && !linkImportActive && fileCount === 0 && (
        <p className="text-xs text-slate-500">
          Загрузите хотя бы один файл, прежде чем отправлять.
        </p>
      )}
      {/* 409 link_import_active устаревает, когда перенос кончился: кнопка снова
          доступна, и красное «идёт перенос» над ней только путало бы. */}
      {error instanceof ApiError && !(error.code === 'link_import_active' && !linkImportActive) && (
        <p className="text-sm text-red-600">{describeSubmitError(error)}</p>
      )}
    </div>
  );
}

function describeSubmitError(err: ApiError): string {
  switch (err.code) {
    case 'no_files':
      return 'Не удалось отправить: нет ни одного файла.';
    case 'already_submitted':
      return 'Этот запрос уже отправлен.';
    case 'link_import_active':
      return 'Идёт перенос с Google Диска';
    case 'forbidden':
    case 'token_expired':
      return 'Ссылка просрочена. Запросите новую у бота.';
    default:
      return `Не удалось отправить (${err.code}). Попробуйте позже.`;
  }
}

function StateBadge({state}: {state: ContentRequestState}) {
  const map: Record<ContentRequestState, {label: string; cls: string}> = {
    uploading: {label: 'Загрузка', cls: 'bg-sky-100 text-sky-800'},
    submitted: {label: 'У контент-менеджера', cls: 'bg-amber-100 text-amber-900'},
    published: {label: 'Залит на OF', cls: 'bg-emerald-100 text-emerald-800'},
  };
  const {label, cls} = map[state];
  return (
    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>
      {label}
    </span>
  );
}

// Список без посилання на сам Drive — це папка Кирила, модель не повинна
// мати туди прямого доступу. Показуємо тільки імена + розмір.
// Номер зліва = порядок аплоаду (Drive віддає файли впорядкованими по
// createdTime asc), щоб модель не лічила руками коли файлів багато.
function DriveFileList({files}: {files: ContentRequestFile[]}) {
  if (files.length === 0) {
    return <p className="text-slate-500 text-sm">Пока ничего не загружено.</p>;
  }
  return (
    <ul className="divide-y divide-slate-200 border border-slate-200 rounded-2xl overflow-hidden bg-white">
      {files.map((f, index) => (
        <li key={f.id} className="p-4 flex items-start gap-3">
          <span className="text-sm font-medium text-slate-400 tabular-nums shrink-0 min-w-[1.75rem] pt-0.5">
            {index + 1}.
          </span>
          <div className="min-w-0 flex-1">
            <p className="break-all font-medium text-slate-900">{f.name}</p>
            <p className="text-xs text-slate-500 mt-0.5 tabular-nums">
              {formatBytes(f.size)} · {formatDateTime(f.uploadedAt)}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function Notice({tone, children}: {tone: 'info' | 'success'; children: React.ReactNode}) {
  const cls =
    tone === 'success'
      ? 'bg-emerald-50 text-emerald-900 border-emerald-200'
      : 'bg-slate-50 text-slate-800 border-slate-200';
  return (
    <div className={`rounded-md border px-3 py-2 text-sm ${cls}`}>{children}</div>
  );
}

function ErrorScreen({title, hint}: {title: string; hint?: string}) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-sm text-center space-y-2">
        <h1 className="text-lg font-semibold text-slate-900">{title}</h1>
        {hint && <p className="text-sm text-slate-600">{hint}</p>}
      </div>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} ГБ`;
}

// Формат "9 мая, 14:30:42" — компактний для inline-показу під назвою файла.
// Без року: усі аплоади свіжі (запит живе короткий час), рік був би шумом.
// Секунди потрібні щоб відрізнити файли залиті в межах однієї хвилини.
function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}
